import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CheckCircle, Clock, Eye, Bell } from 'lucide-react';

interface ProfileUpdateSuccessModalProps {
  open: boolean;
  onClose: () => void;
}

const ProfileUpdateSuccessModal = ({ open, onClose }: ProfileUpdateSuccessModalProps) => {
  return (
    <Dialog open={open} onOpenChange={onClose} modal={true}>
      <DialogContent 
        className="max-w-md w-[90vw] max-h-[85vh] p-0 overflow-hidden mx-4 my-4" 
        onEscapeKeyDown={(e) => e.preventDefault()} 
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        {/* Header with gradient background */}
        <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-4 sm:p-5 text-center">
          <div className="flex justify-center mb-3">
            <CheckCircle className="h-12 w-12 text-white" />
          </div>
          <DialogTitle className="text-lg sm:text-xl font-bold mb-2">
            Profile Update Submitted!
          </DialogTitle>
          <p className="text-blue-100 text-sm">
            Your changes are under admin review
          </p>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
          {/* Status Info */}
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
            <div className="flex items-start gap-3">
              <Clock className="h-4 w-4 text-orange-600 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-orange-900 mb-1 text-sm">
                  Review in Progress
                </h3>
                <p className="text-orange-700 text-xs">
                  Our admin team will review your profile changes within 24-48 hours. 
                  You'll receive a notification once complete.
                </p>
              </div>
            </div>
          </div>

          {/* What's Next */}
            <div className="space-y-3">
              <h3 className="font-semibold text-sm text-gray-900">What happens next?</h3>
            
            <div className="space-y-2">
              <div className="flex items-start gap-2">
                <div className="bg-blue-100 rounded-full p-1">
                  <Eye className="h-3 w-3 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900 text-xs">Admin Review</p>
                  <p className="text-gray-600 text-xs">
                    Changes reviewed for quality and compliance
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-2">
                <div className="bg-green-100 rounded-full p-1">
                  <Bell className="h-3 w-3 text-green-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900 text-xs">Instant Notification</p>
                  <p className="text-gray-600 text-xs">
                    You'll be notified when approved
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-2">
                <div className="bg-purple-100 rounded-full p-1">
                  <CheckCircle className="h-3 w-3 text-purple-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900 text-xs">Go Live</p>
                  <p className="text-gray-600 text-xs">
                    Updated profile visible once approved
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Action Button */}
          <div className="pt-3">
            <Button 
              onClick={onClose}
              className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold py-2 text-sm"
            >
              Got it, thanks!
            </Button>
          </div>

          {/* Help Text */}
          <div className="text-center">
            <p className="text-gray-500 text-xs">
              Questions? Contact our support team anytime
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProfileUpdateSuccessModal;